import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Warning, CheckCircle, XCircle, Info, ArrowsClockwise } from "@phosphor-icons/react";

/**
 * v254 — WhatsApp diagnostic banner.
 * Surfaces the last Twilio outbound errors (with parsed error code) to the
 * admin, so they can instantly understand why messages fail :
 *   - 63112 → Meta disabled WABA
 *   - 63015 → recipient not opted-in to Sandbox
 *   - 21211 → invalid destination phone number
 *   - etc.
 */
export default function WhatsAppDiagnosticBanner() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/settings/whatsapp/diagnostic");
      setData(r.data);
    } catch (e) {
      setData({ error: e?.response?.data?.detail || "Impossible de charger le diagnostic" });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading) return null;
  if (!data) return null;
  if (data.error) return null;

  const {
    total_sent = 0, total_failures = 0, success_rate,
    current_sender, code_breakdown = {},
    failures = [], last_success,
  } = data;

  // Nothing sent in the last 14 days → skip banner (no data to diagnose)
  if (total_sent === 0) {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700 flex items-start gap-3" data-testid="wa-diag-empty">
        <Info size={18} className="text-slate-500 mt-0.5"/>
        <div>Aucun message WhatsApp envoyé depuis 14 jours. Le diagnostic apparaîtra ici dès que MasterTicket essaiera d'envoyer un message.</div>
      </div>
    );
  }

  // All good
  if (total_failures === 0) {
    return (
      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800 flex items-start gap-3" data-testid="wa-diag-ok">
        <CheckCircle size={18} className="text-emerald-600 mt-0.5"/>
        <div>
          <div className="font-medium">WhatsApp opérationnel</div>
          <div className="text-emerald-700/80 text-xs mt-0.5">
            {total_sent} message{total_sent > 1 ? "s" : ""} envoyé{total_sent > 1 ? "s" : ""} sur 14 jours — aucun échec. Sender : <code>{current_sender}</code>.
          </div>
        </div>
      </div>
    );
  }

  // We have failures → dig in
  const topFailure = failures[0]; // most recent
  const isCritical = topFailure?.severity === "critical";
  const bg = isCritical ? "bg-rose-50 border-rose-300"
           : topFailure?.severity === "error" ? "bg-orange-50 border-orange-300"
           : "bg-amber-50 border-amber-300";
  const iconColor = isCritical ? "text-rose-600"
                  : topFailure?.severity === "error" ? "text-orange-600"
                  : "text-amber-600";

  return (
    <div className={`rounded-lg border p-3 space-y-3 ${bg}`} data-testid="wa-diag-banner">
      <div className="flex items-start gap-3">
        {isCritical ? <XCircle size={20} className={iconColor + " mt-0.5"}/> : <Warning size={20} className={iconColor + " mt-0.5"}/>}
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm" data-testid="wa-diag-title">
            {topFailure?.title || `Twilio ${topFailure?.code || ""}`}
            <span className="ml-2 text-xs font-normal text-slate-600">
              ({total_failures}/{total_sent} échec{total_failures > 1 ? "s" : ""} sur 14j
              {success_rate !== null && success_rate !== undefined ? ` — ${Math.round(success_rate * 100)}% de succès` : ""})
            </span>
          </div>
          <div className="text-xs text-slate-700 mt-1" data-testid="wa-diag-hint">
            {topFailure?.hint}
          </div>
          {Object.keys(code_breakdown).length > 1 && (
            <div className="text-[11px] text-slate-600 mt-1.5">
              Autres codes rencontrés :{" "}
              {Object.entries(code_breakdown).map(([code, n]) => (
                <code key={code} className="mr-2">{code} ({n})</code>
              ))}
            </div>
          )}
          {last_success && (
            <div className="text-[11px] text-slate-600 mt-1">
              Dernier envoi OK : <code>{last_success.kind || "?"}</code> à <code>{last_success.to}</code> le{" "}
              {new Date(last_success.sent_at).toLocaleString("fr-FR")}.
            </div>
          )}
        </div>
        <Button size="sm" variant="outline" onClick={load} data-testid="wa-diag-refresh">
          <ArrowsClockwise size={14} className="mr-1"/> Rafraîchir
        </Button>
      </div>
    </div>
  );
}
